import type { TVShowListItem } from '@/services/tvShowService';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { TableAction, TableColumn, TVShowGenre } from '@/types';
import { ArrowUpToLine, Edit, Eye, Trash2, Play, Plus, Search } from 'lucide-react';
import { useTVShowList, useDeleteTVShow, useToggleTVShowStatus, useImportTVShow, usePromoteTVShow } from '@/hooks/useTVShows';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import Toggle from '@/components/ui/Toggle';
import Modal from '@/components/ui/Modal';
import { useCategoriesForDropdown, useConfirmDialog, useTranslation } from '@/hooks';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import AutocompleteSelect from '@/components/ui/AutocompleteSelect';

const TVShows = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { confirm, dialog } = useConfirmDialog();
  const deleteTVShowMutation = useDeleteTVShow();
  const toggleStatusMutation = useToggleTVShowStatus();
  const importTVShowMutation = useImportTVShow();
  const promoteTVShowMutation = usePromoteTVShow();
  const categoryFilter = searchParams.get('categories');
  const { data: categories } = useCategoriesForDropdown('TV_SHOW');
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importForm, setImportForm] = useState({
    id: '',
    token: ''
  });

  const categoryOptions = useMemo(
    () =>
      (categories ?? []).map((category) => ({
        value: category.id,
        label: category.description || category.englishDescription,
      })),
    [categories]
  );

  const initialFilters = useMemo(
    () => ({
      categories: categoryFilter || undefined,
    }),
    [categoryFilter]
  );

  const updateCategoryFilterParam = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);

    if (value) {
      nextParams.set('categories', value);
    } else {
      nextParams.delete('categories');
    }

    setSearchParams(nextParams);
  };

  const tvShowColumns: TableColumn<TVShowListItem>[] = [
    {
      sortable: true,
      key: 'title',
      header: 'Título',
      accessor: (tvShow) => (
        <div className="font-medium text-gray-900">
          <Link to={`/content/tv-shows/${tvShow.id}/edit`} className="link">
            {tvShow.title}
          </Link>
        </div>
      ),
      className: 'min-w-48'
    },
    {
      sortable: true,
      key: 'year',
      header: 'Año',
      accessor: (tvShow) => (
        <div className="text-gray-700">
          {tvShow.year}
        </div>
      )
    },
    {
      key: 'totalEpisodes',
      header: 'Episodios',
      accessor: (tvShow) => (
        <div className="text-gray-700 flex items-center gap-1">
          <Link className="link flex items-center" to={`/content/tv-shows/${tvShow.id}/episodes`}>
            <Play className="w-4 h-4"/>
            {tvShow.totalEpisodes || 'N/A'}
          </Link>
        </div>
      )
    },
    {
      key: 'genres',
      header: 'Géneros',
      accessor: (tvShow) => (
        <div className="text-gray-700">
          <TvShowsGenresList genres={tvShow.genres}/>
        </div>
      ),
      className: 'min-w-32'
    },
    {
      sortable: true,
      key: 'views',
      header: 'Visitas',
      accessor: (tvShow) => (
        <div className="text-gray-700">
          {tvShow.views?.toLocaleString() || 0}
        </div>
      )
    },
    {
      sortable: true,
      key: 'active',
      header: 'Activo',
      accessor: (tvShow) => (
        <Toggle
          checked={tvShow.active}
          onChange={(checked) => handleToggleStatus(tvShow.id, checked)}
          disabled={toggleStatusMutation.isPending}
          size="sm"
        />
      )
    },
    {
      key: 'updatedDate',
      sortable: true,
      header: t.common.fields.updatedDate,
      accessor: (sport) => (
        <div className="text-gray-500">
          {sport.updatedDate ? new Date(sport.updatedDate).toLocaleDateString('es'):'N/D'}
        </div>
      ),
    }
  ];

  const tvShowActions: TableAction<TVShowListItem>[] = [
    {
      label: 'Mover al frente',
      icon: ArrowUpToLine,
      onClick: (tvShow) => handlePromoteTVShow(tvShow.id),
      className: 'text-amber-600 hover:text-amber-800'
    },
    {
      label: 'Ver episodios',
      icon: Eye,
      onClick: (tvShow) => navigate(`/content/tv-shows/${tvShow.id}/episodes`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Editar',
      icon: Edit,
      onClick: (tvShow) => navigate(`/content/tv-shows/${tvShow.id}/edit`),
      className: 'text-gray-700 hover:text-indigo-600'
    },
    {
      label: 'Eliminar',
      icon: Trash2,
      onClick: (tvShow) => handleDeleteTVShow(tvShow.id),
      className: 'text-red-600 hover:text-red-800'
    }
  ];

  const handlePromoteTVShow = async (id: string) => {
    try {
      await promoteTVShowMutation.mutateAsync(id);
      toast.success('Serie movida al frente exitosamente');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const handleCreateTVShow = () => {
    navigate('/content/tv-shows/create');
  };

  const handleDeleteTVShow = async (id: string) => {
    try {
      const confirmed = await confirm({
        title: t.common.confirmationTitle,
        message: '¿Está seguro de que desea eliminar esta serie?',
        confirmLabel: t.buttons.delete,
        cancelLabel: t.buttons.cancel,
        confirmVariant: 'danger',
        onConfirm: async () => {
          await deleteTVShowMutation.mutateAsync(id);
        },
      });

      if (!confirmed) {
        return;
      }
      console.log('Serie eliminada exitosamente');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : t.errors.unexpectedError);
      return;
    }

    toast.success('Serie eliminada exitosamente');
  };

  const handleToggleStatus = async (id: string, active: boolean) => {
    try {
      await toggleStatusMutation.mutateAsync({ id, active });
      console.log('Estado actualizado exitosamente');
    } catch (error) {
      console.error('Toggle status error:', error);
    }
  };

  const handleImportButtonClick = () => {
    setIsImportModalOpen(true);
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setImportForm({ id: '', token: '' });
  };

  const handleImportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await importTVShowMutation.mutateAsync({
        id: importForm.id.trim(),
        token: importForm.token.trim()
      });
      closeImportModal();
    } catch (error) {
      console.error('Import error:', error);
      alert(error instanceof Error ? error.message : 'Error al importar la serie');
    }
  };

  const isImportDisabled = !importForm.id.trim() || !importForm.token.trim() || importTVShowMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <DataTableWithQuery
          showTotalCount
          tableStyle="compact"
          columns={tvShowColumns}
          actions={tvShowActions}
          useQueryHook={useTVShowList}
          onCreate={handleCreateTVShow}
          headerRenderer={({ searchQuery, onSearchChange, searchPlaceholder, totalCount }) => (
            <div className="border-b border-gray-200 bg-white">
              <div className="px-4 py-2 md:hidden">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">Series</h3>
                    <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                      {totalCount}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={handleCreateTVShow} size="sm">
                      <Plus className="mr-2 h-4 w-4" />
                      Crear
                    </Button>
                    <Button type="button" onClick={handleImportButtonClick} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      Importar
                    </Button>
                  </div>
                </div>
                <div className="relative mt-3">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3">
                  <AutocompleteSelect
                    value={categoryFilter ?? ''}
                    onChange={updateCategoryFilterParam}
                    options={categoryOptions}
                    placeholder="Todas las categorías"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
              </div>
              <div className="hidden flex-wrap items-center gap-3 px-4 py-2 md:flex">
                <h3 className="text-lg font-medium text-gray-900">Series</h3>
                <span className="inline-flex items-center rounded-md bg-yellow-500 px-2 py-1 text-sm font-bold text-white">
                  {totalCount}
                </span>
                <div className="relative flex-1 min-w-[220px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchQuery}
                    onChange={onSearchChange}
                    className="w-full rounded-md border border-gray-300 py-1.5 pl-10 pr-4 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                  />
                </div>
                <div className="min-w-[210px]">
                  <AutocompleteSelect
                    value={categoryFilter ?? ''}
                    onChange={updateCategoryFilterParam}
                    options={categoryOptions}
                    placeholder="Todas las categorías"
                    size="sm"
                    className="text-base"
                    allowClear
                  />
                </div>
                <Button onClick={handleCreateTVShow} size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Crear nueva serie
                </Button>
                <Button type="button" onClick={handleImportButtonClick} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  Importar
                </Button>
              </div>
            </div>
          )}
          searchPlaceholder="Buscar series..."
          title="Series"
          itemsPerPage={20}
          initialFilters={initialFilters}
          className="rounded-none shadow-none border border-gray-200"
        />
        {dialog}
      </div>
      <Modal
        isOpen={isImportModalOpen}
        onClose={closeImportModal}
        title="Importar serie"
        size="sm"
      >
        <form onSubmit={handleImportSubmit} className="space-y-4">
          <div>
            <label htmlFor="tv-show-id" className="block text-sm font-medium text-gray-700 mb-1">
              ID
            </label>
            <input
              id="tv-show-id"
              type="text"
              value={importForm.id}
              onChange={(event) => setImportForm((prev) => ({ ...prev, id: event.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Ingresa el ID"
              disabled={importTVShowMutation.isPending}
              required
            />
          </div>
          <div>
            <label htmlFor="tv-show-token" className="block text-sm font-medium text-gray-700 mb-1">
              Token
            </label>
            <input
              id="tv-show-token"
              type="text"
              value={importForm.token}
              onChange={(event) => setImportForm((prev) => ({ ...prev, token: event.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500"
              placeholder="Ingresa el token"
              disabled={importTVShowMutation.isPending}
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={closeImportModal}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-60"
              disabled={importTVShowMutation.isPending}
            >
              Cerrar
            </button>
            <button
              type="submit"
              disabled={isImportDisabled}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {importTVShowMutation.isPending ? 'Importando...' : 'Importar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};


const TvShowsGenresList = ({ genres }: { genres: TVShowGenre[] }) => {
  return (
    <div className="flex flex-wrap gap-1">
      {genres.map((genre) => (
        <span
          key={genre}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-400 text-white"
        >
          {genre.toString().charAt(0).toUpperCase() + genre.toString().slice(1).toLowerCase()}
        </span>
      ))}
    </div>
  );
}


export default TVShows;
